import React, { useState } from "react";
import ClickAwayListener from "react-click-away-listener";
import { useCombobox } from "downshift";
import { useDebouncedCallback } from "use-debounce";
import { SearchApi } from "../Api/SearchApi";
import { SmartSearchItemDto } from "@apiModels/SmartSearch/SmartSearchItemDto.csharp";
import { SmartSearchItemType } from "@apiModels/SmartSearch/SmartSearchItemType.csharp";
import { TreatmentResultItem } from "./TreatmentResultItem";
import { DoctorResultItem } from "./DoctorResultItem";
import { SearchResultItem } from "./SearchResultItem";
import { SmartSearchViewModel } from "../../Partials/TopBar/SmartSearchViewModel.csharp";

const searchApi = new SearchApi();

export const SmartSearch: React.FC<SmartSearchViewModel> = ({
  placeholder,
  searchUrl,
}) => {
  const [showSmartSearch, setShowSmartSearch] = useState(false);
  const [searchItems, setSearchItems] = useState<SmartSearchItemDto[]>([]);
  const hideSmartSearch = () => setShowSmartSearch(false);
  const toggleSmartSearch = () => {
    setShowSmartSearch(!showSmartSearch);
  };

  const search = useDebouncedCallback(async () => {
    const keyword = getInputProps().value;
    if (keyword) {
      const result = await searchApi.smartSearch(keyword);
      setSearchItems([
        ...result,
        {
          type: SmartSearchItemType.Search,
          title: "Søk hele nettstedet",
          url: `${searchUrl}?q=${getInputProps().value}`,
          keywords: [],
          score: 1000,
        },
      ]);
    } else {
      setSearchItems([]);
    }
  }, 500);

  const {
    isOpen,
    getMenuProps,
    getInputProps,
    getComboboxProps,
    highlightedIndex,
    getItemProps,
  } = useCombobox({
    items: searchItems,
    itemToString(item) {
      return item ? item.title : "";
    },
    onInputValueChange: ({ inputValue }) => {
      search();
    },
    onSelectedItemChange: ({ selectedItem: newSelectedItem }) => {
      if (newSelectedItem) {
        window.location.href = newSelectedItem.url;
      }
    },
  });

  const mapSearchDropdown = () => {
    return searchItems.map((item, index) => (
      <li
        {...getItemProps({ item: item })}
        key={index}
        className={
          highlightedIndex === index ? "search-result-highlight" : null
        }
      >
        {item.type == SmartSearchItemType.Doctor && (
          <DoctorResultItem {...item} />
        )}
        {item.type == SmartSearchItemType.Treatment && (
          <TreatmentResultItem {...item} />
        )}
        {item.type == SmartSearchItemType.Search && (
          <SearchResultItem {...item} />
        )}
      </li>
    ));
  };

  const onSubmit = (e: any) => {
    e.preventDefault();
    window.location.href = `${searchUrl}?q=${getInputProps().value}`;
  };

  return (
    <ClickAwayListener onClickAway={hideSmartSearch}>
      <div className="search">
        <button
          className="search-toggler"
          onClick={toggleSmartSearch}
          type="button"
          {...getComboboxProps()}
          aria-controls={...getMenuProps().id}
          aria-expanded={showSmartSearch ? "true" : "false"}
        >
          <i className="icon icon-search" />
          <span className="visually-hidden">&Aring;pne Søk</span>
        </button>
        <div className={`search-execution ${showSmartSearch ? "is-open" : ""}`}>
          {showSmartSearch && (
            <div>
              <form onSubmit={onSubmit}>
                <input
                  placeholder={placeholder}
                  {...getInputProps()}
                  className={
                    "form-control form-control--fullwidth search-input mb-0"
                  }
                  type="search"
                  autoFocus
                />
              </form>
              <div className="search-results" {...getMenuProps()}>
                {isOpen && (
                  <ul className="link-list link-list--bare-bones">
                    {mapSearchDropdown()}
                  </ul>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </ClickAwayListener>
  );
};
